import TextToggle from 'components/UI/TextToggle';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetFemaMetaQuery } from 'services/ipaws/ipaws.api';
import {
  selectAlertMode,
  selectFemaUpdateContext,
  selectIpawsFemaEnvironment,
} from '../../state/selectors/alertCreatorSelectors';
import { setAlertMode, setIpawsFemaEnvironment } from '../../state/slices/alertCreatorSlice';
import { ALERT_MODES } from '../../utils/alertCreator.utils';
import {
  describeUnavailableFemaEnvironments,
  getDefaultFemaEnvironmentChoice,
} from '../../utils/femaEnvironment.utils';
import { describeFemaDestination } from '../../utils/femaMeta.utils';
import FemaEnvironmentSelect from './components/FemaEnvironmentSelect/FemaEnvironmentSelect';

const AlertModeSelector = () => {
  const alertMode = useSelector(selectAlertMode);
  const dispatch = useDispatch();

  const isIpaws = alertMode === ALERT_MODES.IPAWS;

  // The environment comes from the server (GET /v2/fema/meta), never a build-time constant
  // (card FE-01), and stays visible while composing. Asked only once IPAWS is chosen.
  const femaMeta = useGetFemaMetaQuery(undefined, { skip: !isIpaws });

  // Card FE-01 and multi-COG contract §2: the FEMA environment select. The default is the
  // server's `defaultEnvironment` (never production), or the served class from an older backend.
  const storedFemaEnvironment = useSelector(selectIpawsFemaEnvironment);
  const defaultFemaEnvironment = getDefaultFemaEnvironmentChoice(femaMeta.data);
  const isFemaMetaLoaded = Boolean(femaMeta.data) && !femaMeta.isLoading && !femaMeta.isError;
  // The banner describes the chosen class (multi-COG contract §2).
  const destination = describeFemaDestination(femaMeta, storedFemaEnvironment);
  // An Update goes to the class of the original message: the select is locked (contract §1).
  const isUpdating = Boolean(useSelector(selectFemaUpdateContext));
  const unavailableEnvironments = isFemaMetaLoaded
    ? describeUnavailableFemaEnvironments(femaMeta.data)
    : '';

  useEffect(() => {
    if (isIpaws && isFemaMetaLoaded && storedFemaEnvironment === null && defaultFemaEnvironment) {
      dispatch(setIpawsFemaEnvironment(defaultFemaEnvironment));
    }
  }, [dispatch, isFemaMetaLoaded, isIpaws, defaultFemaEnvironment, storedFemaEnvironment]);

  const handleModeChange = (isIpawsSelected) => {
    dispatch(setAlertMode(isIpawsSelected ? ALERT_MODES.IPAWS : ALERT_MODES.RESCALERT));
  };

  return (
    <section
      className={`alert-mode-selector ${
        isIpaws ? 'alert-mode-selector-ipaws' : 'alert-mode-selector-rescalert'
      }`}
      aria-labelledby="alert-destination-heading"
    >
      <h2 id="alert-destination-heading" className="alert-mode-title">
        Alert destination
      </h2>

      <TextToggle
        options={['RescAlert', 'IPAWS']}
        value={isIpaws}
        setValue={handleModeChange}
        parentControlledState
        wrapperClassName="alert-mode-toggle w-100"
        buttonClassName="alert-mode-toggle-button"
        ariaLabel="Alert destination"
      />

      {isIpaws ? (
        <div
          className={`alert-destination-summary alert-destination-summary-ipaws alert-destination-summary-${destination.tone}`}
          role="status"
        >
          <div>
            <strong className="d-block">FEMA IPAWS Public Warning Destination:</strong>
            {/* Until the service answers, why sending is unavailable stays in view. */}
            {destination.tone === 'ready' ? null : (
              <>
                <span className="d-block">{destination.environment}</span>
                <small>{destination.detail}</small>
              </>
            )}
            <FemaEnvironmentSelect
              femaMetaQuery={femaMeta}
              value={storedFemaEnvironment}
              onChange={(choice) => dispatch(setIpawsFemaEnvironment(choice))}
              locked={isUpdating}
              showUnavailableNote={false}
            />
            {/* A ready service without a loaded credential refuses a send; that stays in view. */}
            {destination.tone === 'ready' && destination.detail ? (
              <small className="d-block mt-1">{destination.detail}</small>
            ) : null}
            {/* One line per fact: the select above already names the environment, and the COG
                is masked everywhere it is shown. */}
            <details className="mt-2">
              <summary>Advanced FEMA COG details</summary>
              <div className="mt-1">
                {unavailableEnvironments ? (
                  <small className="d-block">{unavailableEnvironments}</small>
                ) : null}
                {destination.issuingAuthority ? (
                  <span className="d-block">Issuing Authority: {destination.issuingAuthority}</span>
                ) : null}
                <span className="d-block">Credential Status: {destination.credentialStatus}</span>
                <span className="d-block">COG: {destination.cogDisplayId}</span>
                {destination.cogName && destination.cogName !== destination.issuingAuthority ? (
                  <span className="d-block">FEMA COG Name: {destination.cogName}</span>
                ) : null}
                <small className="d-block mt-1">{destination.dissemination}</small>
              </div>
            </details>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default AlertModeSelector;
